import { Component, Property } from '@wonderlandengine/api';
import { vec3, quat } from 'gl-matrix';

export class WeaponSync extends Component {
    static TypeName = 'weapon-sync';

    static Properties = {
        rifle: Property.object(),
        shotgun: Property.object(),
        sniper: Property.object(),
        rightGun: Property.object(), // Right pistol
        leftGun: Property.object(),  // Left pistol
    };

    init () {
        this.currentWeapon = null; // Start with default pistols
        this.activeWeapon = null;
        this.activeWeaponLeft = null; // for akimbo
        this.currentPos = vec3.create();
        this.currentRot = quat.create();
        this.weaponPos = vec3.create();      // Target pos from network
        this.weaponRot = quat.create();      // Target rot from network
        this.weaponPosLeft = vec3.create();  // Target pos for left gun
        this.weaponRotLeft = quat.create();  // Target rot for left gun
        this.currentPosLeft = vec3.create();
        this.currentRotLeft = quat.create();
        // Pre-allocate temp vector for muzzle flash scaling
        this._tempMuzzleScale = vec3.create();
        // PERFORMANCE: Use typed array to eliminate GC and JS-to-WASM conversion overhead
        this._zeroScale = new Float32Array([0.0001, 0.0001, 0.0001]);
        this._tempScale = vec3.create();
        
        this.defaultScale = new Float32Array([0.13, 0.13, 0.13]);
        this.riflesScale = new Float32Array([1.3, 1.3, 1.3]);
        this.MUZZLE_FLASH_RISE = 0.03;   // 30ms to reach full scale
        this.MUZZLE_FLASH_FALL = 0.07;   // 70ms to fade out
        this.MUZZLE_FLASH_TOTAL = this.MUZZLE_FLASH_RISE + this.MUZZLE_FLASH_FALL;
        
        this.muzzleFlashes = {
            rifle: this._findMuzzleFlash(this.rifle),
            shotgun: this._findMuzzleFlash(this.shotgun),
            sniper: this._findMuzzleFlash(this.sniper),
            rightGun: this._findMuzzleFlash(this.rightGun),
            leftGun: this._findMuzzleFlash(this.leftGun),
        };

        this.muzzleFlashTimers = {};
        this.muzzleFlashOriginalScales = {};

        // PERFORMANCE: Flat array cache for zero-GC, high performance update loop
        // Avoiding string-keyed object property lookups in critical paths
        this._mfKeys = ['rifle', 'shotgun', 'sniper', 'rightGun', 'leftGun'];
        this._mfObjs = new Array(5);
        this._mfTimers = new Float32Array(5);
        this._mfOrigScales = new Float32Array(15); // 5 weapons * 3 components (x, y, z)

        for (let i = 0; i < this._mfKeys.length; i++) {
            const key = this._mfKeys[i];
            const mf = this.muzzleFlashes[key];
            this._mfObjs[i] = mf;
            this.muzzleFlashTimers[key] = 0;
            this._mfTimers[i] = 0;
            
            if (mf) {
                const scale = mf.getScalingLocal();
                this.muzzleFlashOriginalScales[key] = [scale[0], scale[1], scale[2]];
                this._mfOrigScales[i * 3] = scale[0];
                this._mfOrigScales[i * 3 + 1] = scale[1];
                this._mfOrigScales[i * 3 + 2] = scale[2];
                mf.setScalingLocal(this._zeroScale); // Initially hide
            } else {
                this.muzzleFlashOriginalScales[key] = [1, 1, 1];
                this._mfOrigScales[i * 3] = 1;
                this._mfOrigScales[i * 3 + 1] = 1;
                this._mfOrigScales[i * 3 + 2] = 1;
            }
        }
    }

    start() {
        if (this.engine && this.engine.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        this.hideWeapons(); // Start with all weapons hidden until set by network
        // Listen for desktop session end (EXIT button)
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
    }

    onDestroy() {
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        }
    }

    onDesktopSessionEnd() {
        this._resetSession();
    }

    onXRSessionEnd() {
        this._resetSession();
    }

    _resetSession() {
        // Hide all weapons and reset muzzle flashes
        this.hideWeapons();
        this.currentWeapon = null; // Reset to default for next session

        // Reset all muzzle flash timers and hide flashes
        for (let i = 0; i < 5; i++) {
            this._mfTimers[i] = 0;
            const mf = this._mfObjs[i];
            if (mf) {
                mf.setScalingLocal(this._zeroScale);
            }
            
            // For backward compatibility
            const key = this._mfKeys[i];
            this.muzzleFlashTimers[key] = 0;
        }
    }

    _findMuzzleFlash(weaponObj) {
        if (!weaponObj || !weaponObj.children) return null;
        return weaponObj.children.length > 0 ? weaponObj.children[0] : null;
    }

    _getWeaponIndex(key) {
        switch (key) {
            case 'rifle': return 0;
            case 'shotgun': return 1;
            case 'sniper': return 2;
            case 'rightGun': return 3;
            case 'leftGun': return 4;
            default: return -1;
        }
    }

    playMuzzleFlash(weaponType, hand) {
        let key = weaponType;
        if (weaponType === 'default') {
            key = hand === 'left' ? 'leftGun' : 'rightGun';
        }
        
        const idx = this._getWeaponIndex(key);
        if (idx !== -1) {
            this.muzzleFlashTimers[key] = 0.1; // 25ms flash for instant feedback
            this._mfTimers[idx] = 0.1;
            
            const mf = this._mfObjs[idx];
            if (mf) {
                // Instantly show muzzle flash at full scale
                this._tempMuzzleScale[0] = this._mfOrigScales[idx * 3];
                this._tempMuzzleScale[1] = this._mfOrigScales[idx * 3 + 1];
                this._tempMuzzleScale[2] = this._mfOrigScales[idx * 3 + 2];
                mf.setScalingLocal(this._tempMuzzleScale);
            }
        }
    }

    setWeapon(weaponType) {
        if (this.currentWeapon === weaponType) {
            return;
        }

        // Hide all muzzle flashes and reset their timers
        for (let i = 0; i < 5; i++) {
            const mf = this._mfObjs[i];
            if (mf) {
                mf.setScalingLocal(this._zeroScale);
            }
            this._mfTimers[i] = 0;
            
            // For backward compatibility
            const key = this._mfKeys[i];
            this.muzzleFlashTimers[key] = 0;
        }

        this._setVisible(this.rifle, false);
        this._setVisible(this.shotgun, false);
        this._setVisible(this.sniper, false);
        this._setVisible(this.rightGun, false);
        this._setVisible(this.leftGun, false);

        this.activeWeapon = null;
        this.activeWeaponLeft = null;

        switch (weaponType) {
            case 'rifle':
                this._setVisible(this.rifle, true);
                this.activeWeapon = this.rifle;
                break;
            case 'shotgun':
                this._setVisible(this.shotgun, true);
                this.activeWeapon = this.shotgun;
                break;
            case 'sniper':
                this._setVisible(this.sniper, true);
                this.activeWeapon = this.sniper;
                break;
            case 'default':
            default:
                this._setVisible(this.rightGun, true);
                this._setVisible(this.leftGun, true);
                this.activeWeapon = this.rightGun;
                this.activeWeaponLeft = this.leftGun;
                break;
        }

        this.currentWeapon = weaponType;
    }

    setTransform(position, rotation, leftPosition, leftRotation) {
        vec3.copy(this.weaponPos, position);
        quat.copy(this.weaponRot, rotation);
        if (leftPosition && leftRotation) {
            vec3.copy(this.weaponPosLeft, leftPosition);
            quat.copy(this.weaponRotLeft, leftRotation);
        }
        this._lastTransformFrame = this._totalFrames || 0;
    }

    /**
     * Hide all weapon meshes (for player deactivation or pooling)
     */
    hideWeapons() {
        this._setVisible(this.rifle, false);
        this._setVisible(this.shotgun, false);
        this._setVisible(this.sniper, false);
        this._setVisible(this.rightGun, false);
        this._setVisible(this.leftGun, false);
        this.activeWeapon = null;
        this.activeWeaponLeft = null;
    }

    /**
     * Show the currently selected weapon(s) (for player reactivation)
     */
    showWeapons() {
        if (!this.currentWeapon) return;
        switch (this.currentWeapon) {
            case 'rifle':
                this._setVisible(this.rifle, true);
                this.activeWeapon = this.rifle;
                break;
            case 'shotgun':
                this._setVisible(this.shotgun, true);
                this.activeWeapon = this.shotgun;
                break;
            case 'sniper':
                this._setVisible(this.sniper, true);
                this.activeWeapon = this.sniper;
                break;
            case 'default':
                this._setVisible(this.rightGun, true);
                this._setVisible(this.leftGun, true);
                this.activeWeapon = this.rightGun;
                this.activeWeaponLeft = this.leftGun;
                break;
        }
    }

    update(dt) {
         for (let i = 0; i < 5; i++) {
            if (this._mfTimers[i] > 0) {
                this._mfTimers[i] -= dt;
                const remaining = this._mfTimers[i];
                const mf = this._mfObjs[i];
                
                if (mf) {
                    if (remaining <= 0) {
                        mf.setScalingLocal(this._zeroScale);
                        // Also sync back to compatibility object just in case
                        this.muzzleFlashTimers[this._mfKeys[i]] = 0;
                    } else if (remaining > this.MUZZLE_FLASH_FALL) {
                        const t = 1 - ((remaining - this.MUZZLE_FLASH_FALL) / this.MUZZLE_FLASH_RISE);
                        // PERFORMANCE: Manual unrolling eliminates vec3.scale() call overhead
                        this._tempMuzzleScale[0] = this._mfOrigScales[i * 3] * t;
                        this._tempMuzzleScale[1] = this._mfOrigScales[i * 3 + 1] * t;
                        this._tempMuzzleScale[2] = this._mfOrigScales[i * 3 + 2] * t;
                        mf.setScalingLocal(this._tempMuzzleScale);
                    } else {
                        const t = remaining / this.MUZZLE_FLASH_FALL;
                        this._tempMuzzleScale[0] = this._mfOrigScales[i * 3] * t;
                        this._tempMuzzleScale[1] = this._mfOrigScales[i * 3 + 1] * t;
                        this._tempMuzzleScale[2] = this._mfOrigScales[i * 3 + 2] * t;
                        mf.setScalingLocal(this._tempMuzzleScale);
                    }
                }
            }
        }

        if (!this.activeWeapon) return;
        this.activeWeapon.getPositionWorld(this.currentPos);
        this.activeWeapon.getRotationWorld(this.currentRot);
        vec3.lerp(this.weaponPos, this.currentPos, this.weaponPos, 10 * dt);
        quat.slerp(this.weaponRot, this.currentRot, this.weaponRot, 10 * dt);
        // Set position and rotation directly from network data
        this.activeWeapon.setPositionWorld(this.weaponPos);
        this.activeWeapon.setRotationWorld(this.weaponRot);
        if (this.activeWeaponLeft) {
            this.activeWeaponLeft.getPositionWorld(this.currentPosLeft);
            this.activeWeaponLeft.getRotationWorld(this.currentRotLeft);
            vec3.lerp(this.weaponPosLeft, this.currentPosLeft, this.weaponPosLeft, 10 * dt);
            quat.slerp(this.weaponRotLeft, this.currentRotLeft, this.weaponRotLeft, 10 * dt);
            this.activeWeaponLeft.setPositionWorld(this.weaponPosLeft);
            this.activeWeaponLeft.setRotationWorld(this.weaponRotLeft);
        }
    }

    _setVisible(obj, visible) {
        if (!obj) return;
        
        // PERFORMANCE: Disable object completely to cull draw calls and avoid transform updates 
        // when hidden, saving both GPU and CPU. Setting active handles hierarchy culling cleanly.
        if (obj.active !== undefined) {
            obj.active = visible;
        }

        if (visible) {
            if (obj === this.rightGun || obj === this.leftGun) {
                obj.setScalingLocal(this.defaultScale);
            } else {
                obj.setScalingLocal(this.riflesScale);
            }
        } else {
            // Hide the weapon by setting scale to zero as fallback
            obj.setScalingLocal(this._zeroScale);
        }
    }
}
